import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useGlobalAdmin } from '@/hooks/useGlobalAdmin';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useToast } from '@/hooks/use-toast';
import { Settings, ArrowLeft, Loader2, MapPin, Globe, Mail, Image, Upload, Trash2, Palette, Home, FileText, BookOpen, Building, ChevronRight, ChevronDown, Sparkles, Search } from 'lucide-react';
import { SiteAdManager } from '@/components/admin/SiteAdManager';
import { ThemeSelector } from '@/components/theme/ThemeSelector';
import { FontSelector } from '@/components/theme/FontSelector';
import { IndexingStatusPanel } from '@/components/admin/IndexingStatusPanel';
import { SiteSwitcher } from '@/components/admin/SiteSwitcher';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
type LeadNotificationFilter = 'all' | 'agent_only' | 'loan_officer_only';

interface NotificationRecipient {
  email: string;
  filter: LeadNotificationFilter;
}

// Helper to parse notification emails from DB (handles migration from string[] to object[])
function parseNotificationEmails(data: unknown): NotificationRecipient[] {
  if (!data || !Array.isArray(data)) return [];
  return data.map((item: unknown) => {
    if (typeof item === 'string') {
      // Old format: just email string - default to 'all'
      return { email: item, filter: 'all' as LeadNotificationFilter };
    }
    if (typeof item === 'object' && item !== null && 'email' in item) {
      const obj = item as { email: string; filter?: string };
      return {
        email: obj.email,
        filter: (obj.filter as LeadNotificationFilter) || 'all',
      };
    }
    return null;
  }).filter((r): r is NotificationRecipient => r !== null);
}

interface SiteData {
  id: string;
  name: string;
  location_served: string;
  owner_email: string;
  primary_domain: string | null;
  canonical_base_url: string | null;
  status: string;
  tagline: string | null;
  description: string | null;
  company_name: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  contact_address: string | null;
  contact_city: string | null;
  contact_state: string | null;
  contact_zip: string | null;
  contact_country: string | null;
  is_enabled: boolean;
  favicon_url: string | null;
  theme: string;
  header_font: string | null;
  use_home_page: boolean;
  realtor_assigned: boolean;
  enable_market_value_report: boolean;
  enable_community_guide: boolean;
  enable_mortgage_info: boolean;
  seller_ad_image_url: string | null;
  buyer_ad_image_url: string | null;
  mortgage_ad_image_url: string | null;
  blog_enabled: boolean;
  allow_multiple_blog_posts_per_day: boolean;
  blog_timezone: string | null;
  lead_notification_enabled: boolean;
  lead_notification_emails: NotificationRecipient[] | null;
}

export default function SiteSettings() {
  const { user, loading: authLoading } = useAuth();
  const { selectedSiteId, loading: globalAdminLoading, refetchSites } = useGlobalAdmin();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [site, setSite] = useState<SiteData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const [generatingDescription, setGeneratingDescription] = useState(false);
  const [generatingTagline, setGeneratingTagline] = useState(false);
  const faviconInputRef = useRef<HTMLInputElement>(null);
  
  // Collapsible section states
  const [themeOpen, setThemeOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [leadCaptureOpen, setLeadCaptureOpen] = useState(false);
  const [indexingOpen, setIndexingOpen] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    location_served: '',
    company_name: '',
    tagline: '',
    description: '',
    primary_domain: '',
    canonical_base_url: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    contact_address: '',
    contact_city: '',
    contact_state: '',
    contact_zip: '',
    contact_country: 'USA',
    is_enabled: true,
    favicon_url: '',
    theme: 'classic',
    header_font: 'playfair-display',
    use_home_page: true,
    realtor_assigned: true,
    enable_market_value_report: true,
    enable_community_guide: true,
    enable_mortgage_info: true,
    seller_ad_image_url: '',
    buyer_ad_image_url: '',
    mortgage_ad_image_url: '',
    blog_enabled: false,
    allow_multiple_blog_posts_per_day: false,
    blog_timezone: 'America/New_York',
    lead_notification_enabled: false,
    lead_notification_emails: [] as NotificationRecipient[],
  });

  const [newNotificationEmail, setNewNotificationEmail] = useState('');
  const [newNotificationFilter, setNewNotificationFilter] = useState<LeadNotificationFilter>('all');

  // Helper function to add a new notification email
  const addNotificationEmail = () => {
    if (!newNotificationEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newNotificationEmail)) {
      toast({
        title: 'Invalid email',
        description: 'Please enter a valid email address.',
        variant: 'destructive',
      });
      return;
    }
    
    if (formData.lead_notification_emails.some(r => r.email === newNotificationEmail)) {
      toast({
        title: 'Email already added',
        description: 'This email address is already in the list.',
        variant: 'destructive',
      });
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      lead_notification_emails: [...prev.lead_notification_emails, { email: newNotificationEmail, filter: newNotificationFilter }]
    }));
    setNewNotificationEmail('');
    setNewNotificationFilter('all');
  };

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/admin/login');
      return;
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (selectedSiteId) {
      fetchSite();
    }
  }, [selectedSiteId]);

  const fetchSite = async () => {
    if (!selectedSiteId) return;
    
    setLoading(true);
    try {
      const { data: siteData, error } = await supabase
        .from('sites')
        .select('*')
        .eq('id', selectedSiteId)
        .single();

      if (error) throw error;

      if (siteData) {
        const typedSite = siteData as unknown as SiteData;
        setSite(typedSite);
        setFormData({
          name: typedSite.name || '',
          location_served: typedSite.location_served || '',
          company_name: typedSite.company_name || '',
          tagline: typedSite.tagline || '',
          description: (typedSite as any).description || '',
          primary_domain: typedSite.primary_domain || '',
          canonical_base_url: typedSite.canonical_base_url || '',
          contact_name: typedSite.contact_name || '',
          contact_email: typedSite.contact_email || typedSite.owner_email || '',
          contact_phone: typedSite.contact_phone || '',
          contact_address: typedSite.contact_address || '',
          contact_city: typedSite.contact_city || '',
          contact_state: typedSite.contact_state || '',
          contact_zip: typedSite.contact_zip || '',
          contact_country: typedSite.contact_country || 'USA',
          is_enabled: typedSite.is_enabled ?? true,
          favicon_url: typedSite.favicon_url || '',
          theme: typedSite.theme || 'classic',
          header_font: typedSite.header_font || 'playfair-display',
          use_home_page: typedSite.use_home_page ?? true,
          realtor_assigned: typedSite.realtor_assigned ?? true,
          enable_market_value_report: (typedSite as any).enable_market_value_report ?? true,
          enable_community_guide: (typedSite as any).enable_community_guide ?? true,
          enable_mortgage_info: (typedSite as any).enable_mortgage_info ?? true,
          seller_ad_image_url: (typedSite as any).seller_ad_image_url || '',
          buyer_ad_image_url: (typedSite as any).buyer_ad_image_url || '',
          mortgage_ad_image_url: (typedSite as any).mortgage_ad_image_url || '',
          blog_enabled: (typedSite as any).blog_enabled ?? false,
          allow_multiple_blog_posts_per_day: (typedSite as any).allow_multiple_blog_posts_per_day ?? false,
          blog_timezone: (typedSite as any).blog_timezone || 'America/New_York',
          lead_notification_enabled: (typedSite as any).lead_notification_enabled ?? false,
          lead_notification_emails: parseNotificationEmails((typedSite as any).lead_notification_emails),
        });
      }
    } catch (error) {
      console.error('Error fetching site:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleGenerateDescription = async () => {
    if (!formData.name || !formData.location_served) {
      toast({
        title: "Missing information",
        description: "Please fill in the Website Name and Location Served fields first.",
        variant: "destructive",
      });
      return;
    }

    setGeneratingDescription(true);
    try {
      const response = await supabase.functions.invoke('generate-site-description', {
        body: {
          siteName: formData.name,
          location: formData.location_served,
          tagline: formData.tagline || null,
        },
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      if (response.data?.description) {
        setFormData(prev => ({ ...prev, description: response.data.description }));
        toast({
          title: "Description generated",
          description: "Your new SEO description has been created. Don't forget to save!",
        });
      }
    } catch (error) {
      console.error('Error generating description:', error);
      toast({
        title: "Generation failed",
        description: error instanceof Error ? error.message : "Failed to generate description",
        variant: "destructive",
      });
    } finally {
      setGeneratingDescription(false);
    }
  };

  const handleGenerateTagline = async () => {
    if (!formData.name || !formData.location_served) {
      toast({
        title: "Missing information",
        description: "Please fill in the Website Name and Location Served fields first.",
        variant: "destructive",
      });
      return;
    }

    setGeneratingTagline(true);
    try {
      const response = await supabase.functions.invoke('generate-site-tagline', {
        body: {
          siteName: formData.name,
          location: formData.location_served,
        },
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      if (response.data?.tagline) {
        setFormData(prev => ({ ...prev, tagline: response.data.tagline }));
        toast({
          title: "Tagline generated",
          description: "Your new tagline has been created. Don't forget to save!",
        });
      }
    } catch (error) {
      console.error('Error generating tagline:', error);
      toast({
        title: "Generation failed",
        description: error instanceof Error ? error.message : "Failed to generate tagline",
        variant: "destructive",
      });
    } finally {
      setGeneratingTagline(false);
    }
  };

  const handleSave = async () => {
    if (!site) return;

    // Track if site is being enabled (was disabled, now enabled)
    const siteBeingEnabled = !site.is_enabled && formData.is_enabled;

    setSaving(true);
    try {
      const updatedData = {
        name: formData.name,
        location_served: formData.location_served,
        company_name: formData.company_name || null,
        tagline: formData.tagline || null,
        description: formData.description || null,
        primary_domain: formData.primary_domain || null,
        canonical_base_url: formData.canonical_base_url || null,
        contact_name: formData.contact_name || null,
        contact_email: formData.contact_email || null,
        contact_phone: formData.contact_phone || null,
        contact_address: formData.contact_address || null,
        contact_city: formData.contact_city || null,
        contact_state: formData.contact_state || null,
        contact_zip: formData.contact_zip || null,
        contact_country: formData.contact_country || null,
        is_enabled: formData.is_enabled,
        status: formData.is_enabled ? 'active' : 'pending',
        favicon_url: formData.favicon_url || null,
        theme: formData.theme,
        header_font: formData.header_font,
        use_home_page: formData.use_home_page,
        realtor_assigned: formData.realtor_assigned,
        enable_market_value_report: formData.enable_market_value_report,
        enable_community_guide: formData.enable_community_guide,
        enable_mortgage_info: formData.enable_mortgage_info,
        seller_ad_image_url: formData.seller_ad_image_url || null,
        buyer_ad_image_url: formData.buyer_ad_image_url || null,
        mortgage_ad_image_url: formData.mortgage_ad_image_url || null,
        blog_enabled: formData.blog_enabled,
        allow_multiple_blog_posts_per_day: formData.allow_multiple_blog_posts_per_day,
        blog_timezone: formData.blog_timezone || 'America/New_York',
        lead_notification_enabled: formData.lead_notification_enabled,
        lead_notification_emails: formData.lead_notification_emails.length > 0 ? formData.lead_notification_emails : [],
      };

      const { error } = await supabase
        .from('sites')
        .update(updatedData as any)
        .eq('id', site.id);

      if (error) throw error;

      // Update local site state to match saved data
      setSite(prev => prev ? { ...prev, ...updatedData } as SiteData : null);

      // Auto-trigger IndexNow when site is enabled
      if (siteBeingEnabled) {
        supabase.functions.invoke('auto-indexnow', {
          body: { siteId: site.id, trigger: 'site_enabled' }
        }).then(({ error }) => {
          if (error) {
            console.error('Auto-IndexNow error:', error);
          } else {
            console.log('Auto-IndexNow triggered for site enablement');
          }
        });
      }

      // Refresh sites list to update site switcher with new domain
      await refetchSites();

      toast({
        title: 'Settings saved',
        description: 'Your site settings have been updated.',
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to save settings. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleFaviconUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !site) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file type',
        description: 'Please upload an image file (PNG, ICO, JPG)',
        variant: 'destructive',
      });
      return;
    }

    if (file.size > 500 * 1024) {
      toast({
        title: 'File too large',
        description: 'Favicon must be under 500KB',
        variant: 'destructive',
      });
      return;
    }

    setUploadingFavicon(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `sites/${site.id}/favicon-${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('town-images')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('town-images')
        .getPublicUrl(fileName);

      setFormData(prev => ({ ...prev, favicon_url: publicUrl }));
      
      toast({
        title: 'Favicon uploaded',
        description: 'Remember to save your settings to apply the change.',
      });
    } catch (error) {
      console.error('Error uploading favicon:', error);
      toast({
        title: 'Upload failed',
        description: 'Failed to upload favicon. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setUploadingFavicon(false);
      if (faviconInputRef.current) {
        faviconInputRef.current.value = '';
      }
    }
  };

  const handleRemoveFavicon = () => {
    setFormData(prev => ({ ...prev, favicon_url: '' }));
  };

  if (authLoading || globalAdminLoading || loading) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user || !site) return null;

  return (
    <div className="min-h-screen bg-muted">
      {/* Header */}
      <header className="bg-background border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Settings className="h-6 w-6 text-primary" />
              <span className="font-display text-xl">Site Settings</span>
            </div>
            <div className="flex items-center gap-4">
              <SiteSwitcher />
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-display font-bold">Site Settings</h1>
            <p className="text-muted-foreground mt-1">
              Configure your site's branding, location, and contact information.
            </p>
          </div>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Settings'
            )}
          </Button>
        </div>

        {/* Quick Settings Row - Site Status, Home Page, Realtor Assignment */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Site Status */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`w-2.5 h-2.5 rounded-full ${formData.is_enabled ? 'bg-estate-success' : 'bg-destructive'}`} />
                  <Label htmlFor="is_enabled" className="text-sm font-medium">Site Enabled</Label>
                </div>
                <Switch
                  id="is_enabled"
                  checked={formData.is_enabled}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_enabled: checked }))}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {formData.is_enabled ? 'Live and accessible' : 'Disabled'}
              </p>
            </CardContent>
          </Card>

          {/* Home Page */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`w-2.5 h-2.5 rounded-full ${formData.use_home_page ? 'bg-estate-success' : 'bg-muted-foreground'}`} />
                  <Label htmlFor="use_home_page" className="text-sm font-medium">Home Page</Label>
                </div>
                <Switch
                  id="use_home_page"
                  checked={formData.use_home_page}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, use_home_page: checked }))}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {formData.use_home_page ? 'Show home page' : 'Redirect to town'}
              </p>
            </CardContent>
          </Card>

          {/* Realtor Assignment */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`w-2.5 h-2.5 rounded-full ${formData.realtor_assigned ? 'bg-estate-success' : 'bg-muted-foreground'}`} />
                  <Label htmlFor="realtor_assigned" className="text-sm font-medium">Realtor Assigned</Label>
                </div>
                <Switch
                  id="realtor_assigned"
                  checked={formData.realtor_assigned}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, realtor_assigned: checked }))}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {formData.realtor_assigned ? 'Agent branding shown' : 'No agent branding'}
              </p>
              {formData.realtor_assigned && (
                <Button
                  variant="link"
                  size="sm"
                  className="h-auto p-0 mt-1 text-xs"
                  onClick={() => navigate('/admin/profile')}
                >
                  Edit Realtor Card →
                </Button>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Location Served */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Location Served
            </CardTitle>
            <CardDescription>The geographic area your business covers</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="location_served">Location</Label>
              <Input
                id="location_served"
                name="location_served"
                value={formData.location_served}
                onChange={handleInputChange}
                placeholder="e.g., South Coast, Massachusetts"
              />
              <p className="text-xs text-muted-foreground mt-1">
                This appears in your site's branding and SEO
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-3 pt-2">
              <Button 
                variant="outline" 
                className="w-full justify-between"
                onClick={() => navigate('/admin/towns')}
              >
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  <span>Manage Towns</span>
                </div>
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-between"
                onClick={() => navigate('/admin/areas')}
              >
                <div className="flex items-center gap-2">
                  <Building className="h-4 w-4" />
                  <span>Manage Neighborhoods</span>
                </div>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>


        {/* Branding */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Site Branding</CardTitle>
            <CardDescription>Your business name, tagline, and browser icon</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="name">Website Name</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="e.g., South Coast Homes"
              />
              <p className="text-xs text-muted-foreground mt-1">
                The name of your website (appears in header and browser tab)
              </p>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label htmlFor="tagline">Tagline</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleGenerateTagline}
                  disabled={generatingTagline}
                  className="h-7 text-xs"
                >
                  {generatingTagline ? (
                    <>
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-3 w-3 mr-1" />
                      Generate with AI
                    </>
                  )}
                </Button>
              </div>
              <Input
                id="tagline"
                name="tagline"
                value={formData.tagline}
                onChange={handleInputChange}
                placeholder="e.g., Your Trusted Local Real Estate Partner"
              />
              <p className="text-xs text-muted-foreground mt-1">
                A short phrase displayed prominently on your site (appears in search results)
              </p>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label htmlFor="description">Description</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleGenerateDescription}
                  disabled={generatingDescription}
                  className="h-7 text-xs"
                >
                  {generatingDescription ? (
                    <>
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-3 w-3 mr-1" />
                      Generate with AI
                    </>
                  )}
                </Button>
              </div>
              <Textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="e.g., Find your perfect home with expert local guidance. We specialize in residential properties across the South Coast region."
                rows={3}
                className="resize-none"
              />
              <p className="text-xs text-muted-foreground mt-1">
                A 1-2 sentence description for search engine results (up to 160 characters recommended)
              </p>
            </div>
            
            {/* Favicon Upload */}
            <div>
              <Label>Browser Icon (Favicon)</Label>
              <p className="text-xs text-muted-foreground mb-2">
                The small icon that appears in browser tabs. Recommended: 32x32 or 64x64 pixels, PNG or ICO format.
              </p>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 border rounded-lg flex items-center justify-center bg-muted overflow-hidden">
                  {formData.favicon_url ? (
                    <img 
                      src={formData.favicon_url} 
                      alt="Favicon preview" 
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <Image className="h-6 w-6 text-muted-foreground" />
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <input
                    ref={faviconInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFaviconUpload}
                    className="hidden"
                    id="favicon-upload"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => faviconInputRef.current?.click()}
                    disabled={uploadingFavicon}
                  >
                    {uploadingFavicon ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Upload Icon
                      </>
                    )}
                  </Button>
                  {formData.favicon_url && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleRemoveFavicon}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Domain Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Domain Settings
            </CardTitle>
            <CardDescription>Configure your custom domain</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="primary_domain">Primary Domain</Label>
              <Input
                id="primary_domain"
                name="primary_domain"
                value={formData.primary_domain}
                onChange={handleInputChange}
                placeholder="e.g., www.smithrealty.com"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Your custom domain (requires DNS configuration)
              </p>
            </div>
            <div>
              <Label htmlFor="canonical_base_url">Canonical Base URL</Label>
              <Input
                id="canonical_base_url"
                name="canonical_base_url"
                value={formData.canonical_base_url}
                onChange={handleInputChange}
                placeholder="e.g., https://www.smithrealty.com"
              />
              <p className="text-xs text-muted-foreground mt-1">
                The full URL used for SEO canonical tags
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Blog Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              Blog Settings
            </CardTitle>
            <CardDescription>Configure automatic blog post generation</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="blog_enabled" className="text-base">Enable Blog</Label>
                <p className="text-sm text-muted-foreground">
                  {formData.blog_enabled 
                    ? 'Blog is enabled. New posts will be generated daily.' 
                    : 'Blog is disabled. No automatic posts will be created.'}
                </p>
              </div>
              <Switch
                id="blog_enabled"
                checked={formData.blog_enabled}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, blog_enabled: checked }))}
              />
            </div>

            {formData.blog_enabled && (
              <>
                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <div>
                    <Label htmlFor="allow_multiple_blog_posts_per_day" className="text-base">Allow Multiple Posts Per Day</Label>
                    <p className="text-sm text-muted-foreground">
                      Override the one-post-per-day limit for manual creation
                    </p>
                  </div>
                  <Switch
                    id="allow_multiple_blog_posts_per_day"
                    checked={formData.allow_multiple_blog_posts_per_day}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, allow_multiple_blog_posts_per_day: checked }))}
                  />
                </div>

                <div className="pt-4 border-t border-border">
                  <Label htmlFor="blog_timezone">Timezone</Label>
                  <select
                    id="blog_timezone"
                    value={formData.blog_timezone}
                    onChange={(e) => setFormData(prev => ({ ...prev, blog_timezone: e.target.value }))}
                    className="w-full mt-1 px-3 py-2 border border-input rounded-md bg-background"
                  >
                    <option value="America/New_York">Eastern Time (ET)</option>
                    <option value="America/Chicago">Central Time (CT)</option>
                    <option value="America/Denver">Mountain Time (MT)</option>
                    <option value="America/Los_Angeles">Pacific Time (PT)</option>
                    <option value="America/Anchorage">Alaska Time (AKT)</option>
                    <option value="Pacific/Honolulu">Hawaii Time (HT)</option>
                  </select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Posts will be scheduled using this timezone
                  </p>
                </div>

                <div className="pt-4 border-t border-border">
                  <Button
                    variant="outline"
                    onClick={() => navigate('/admin/blog')}
                  >
                    <BookOpen className="h-4 w-4 mr-2" />
                    Manage Blog Posts
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Theme Settings */}
        <Collapsible open={themeOpen} onOpenChange={setThemeOpen}>
          <Card className="mb-6">
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Palette className="h-5 w-5" />
                    <div>
                      <CardTitle className="text-lg">Page Theme</CardTitle>
                      <CardDescription>
                        Choose a color theme for your town and neighborhood pages
                      </CardDescription>
                    </div>
                  </div>
                  <ChevronDown className={`h-5 w-5 text-muted-foreground transition-transform ${themeOpen ? 'rotate-180' : ''}`} />
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="space-y-6">
                <div>
                  <Label className="text-base font-medium mb-3 block">Color Theme</Label>
                  <ThemeSelector
                    selectedTheme={formData.theme}
                    onThemeChange={(theme) => setFormData(prev => ({ ...prev, theme }))}
                  />
                </div>
                
                <div>
                  <Label className="text-base font-medium mb-3 block">Header Font</Label>
                  <p className="text-xs text-muted-foreground mb-3">
                    Choose a font for headings on your town and neighborhood pages
                  </p>
                  <FontSelector
                    selectedFont={formData.header_font}
                    onFontChange={(font) => setFormData(prev => ({ ...prev, header_font: font }))}
                  />
                </div>
                
                <p className="text-xs text-muted-foreground">
                  Theme and font changes apply to all published town and neighborhood pages. Admin pages are not affected.
                </p>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Business Contact Settings */}
        <Collapsible open={contactOpen} onOpenChange={setContactOpen}>
          <Card className="mb-6">
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Mail className="h-5 w-5" />
                    <div>
                      <CardTitle className="text-lg">Business Contact Settings</CardTitle>
                      <CardDescription>For business use only</CardDescription>
                    </div>
                  </div>
                  <ChevronDown className={`h-5 w-5 text-muted-foreground transition-transform ${contactOpen ? 'rotate-180' : ''}`} />
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="company_name">Company Name</Label>
                    <Input
                      id="company_name"
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleInputChange}
                      placeholder="e.g., Smith Realty Group"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_name">Contact Name</Label>
                    <Input
                      id="contact_name"
                      name="contact_name"
                      value={formData.contact_name}
                      onChange={handleInputChange}
                      placeholder="e.g., John Smith"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="contact_email">Contact Email</Label>
                    <Input
                      id="contact_email"
                      name="contact_email"
                      type="email"
                      value={formData.contact_email}
                      onChange={handleInputChange}
                      placeholder="e.g., contact@smithrealty.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_phone">Contact Phone</Label>
                    <Input
                      id="contact_phone"
                      name="contact_phone"
                      value={formData.contact_phone}
                      onChange={handleInputChange}
                      placeholder="e.g., (508) 555-1234"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="contact_address">Street Address</Label>
                    <Input
                      id="contact_address"
                      name="contact_address"
                      value={formData.contact_address}
                      onChange={handleInputChange}
                      placeholder="e.g., 123 Main Street"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_city">City</Label>
                    <Input
                      id="contact_city"
                      name="contact_city"
                      value={formData.contact_city}
                      onChange={handleInputChange}
                      placeholder="e.g., Boston"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="contact_state">State</Label>
                    <Input
                      id="contact_state"
                      name="contact_state"
                      value={formData.contact_state}
                      onChange={handleInputChange}
                      placeholder="e.g., MA"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_zip">ZIP Code</Label>
                    <Input
                      id="contact_zip"
                      name="contact_zip"
                      value={formData.contact_zip}
                      onChange={handleInputChange}
                      placeholder="e.g., 02101"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_country">Country</Label>
                    <Input
                      id="contact_country"
                      name="contact_country"
                      value={formData.contact_country}
                      onChange={handleInputChange}
                      placeholder="e.g., USA"
                    />
                  </div>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Lead Capture Offers */}
        <Collapsible open={leadCaptureOpen} onOpenChange={setLeadCaptureOpen}>
          <Card className="mb-6">
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    <div>
                      <CardTitle className="text-lg">Lead Capture Offers</CardTitle>
                      <CardDescription>Control which lead capture landing pages and ads are shown</CardDescription>
                    </div>
                  </div>
                  <ChevronDown className={`h-5 w-5 text-muted-foreground transition-transform ${leadCaptureOpen ? 'rotate-180' : ''}`} />
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="space-y-8">
                {/* Market Value Report */}
                <div className="border-b border-border pb-6">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <Label htmlFor="enable_market_value_report" className="text-base">Market Value Report</Label>
                      <p className="text-sm text-muted-foreground">
                        {formData.enable_market_value_report 
                          ? 'The seller ad and /market-value-report page are visible.' 
                          : 'The seller ad and landing page are hidden from visitors.'}
                      </p>
                    </div>
                    <Switch
                      id="enable_market_value_report"
                      checked={formData.enable_market_value_report}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, enable_market_value_report: checked }))}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Manage the ad image in your <a href="/admin/profile" className="text-primary underline hover:no-underline">Profile settings</a>.
                  </p>
                </div>

                {/* Community Guide */}
                <div className="border-b border-border pb-6">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <Label htmlFor="enable_community_guide" className="text-base">Community Guide</Label>
                      <p className="text-sm text-muted-foreground">
                        {formData.enable_community_guide 
                          ? 'The buyer ad and /community-guide page are visible.' 
                          : 'The buyer ad and landing page are hidden from visitors.'}
                      </p>
                    </div>
                    <Switch
                      id="enable_community_guide"
                      checked={formData.enable_community_guide}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, enable_community_guide: checked }))}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Manage the ad image in your <a href="/admin/profile" className="text-primary underline hover:no-underline">Profile settings</a>.
                  </p>
                </div>

                {/* Mortgage Information */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <Label htmlFor="enable_mortgage_info" className="text-base">Mortgage Information</Label>
                      <p className="text-sm text-muted-foreground">
                        {formData.enable_mortgage_info 
                          ? 'The mortgage ad and /mortgages page are visible.' 
                          : 'The mortgage ad and landing page are hidden from visitors.'}
                      </p>
                    </div>
                    <Switch
                      id="enable_mortgage_info"
                      checked={formData.enable_mortgage_info}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, enable_mortgage_info: checked }))}
                    />
                  </div>
                  {site && (
                    <SiteAdManager
                      siteId={site.id}
                      adType="mortgage"
                      onActiveImageChange={(url) => setFormData(prev => ({ ...prev, mortgage_ad_image_url: url }))}
                    />
                  )}
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Lead Email Notifications */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Lead Email Notifications
            </CardTitle>
            <CardDescription>
              Receive email notifications when new leads are submitted
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="lead_notification_enabled" className="text-base">Send Email When Lead Arrives</Label>
                <p className="text-sm text-muted-foreground">
                  {formData.lead_notification_enabled 
                    ? 'You will receive an email notification for each new lead.' 
                    : 'Email notifications are disabled.'}
                </p>
              </div>
              <Switch
                id="lead_notification_enabled"
                checked={formData.lead_notification_enabled}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, lead_notification_enabled: checked }))}
              />
            </div>

            {formData.lead_notification_enabled && (
              <div className="pt-4 border-t border-border space-y-6">
                {/* Notification Email Addresses */}
                <div>
                  <Label className="text-base mb-2 block">Notification Recipients</Label>
                  <p className="text-sm text-muted-foreground mb-3">
                    Add email addresses and assign which types of leads each should receive
                  </p>
                  
                  {/* Current emails with filter badges */}
                  {formData.lead_notification_emails.length > 0 && (
                    <div className="space-y-2 mb-4">
                      {formData.lead_notification_emails.map((recipient, index) => (
                        <div key={index} className="flex items-center gap-2 bg-muted/50 p-2 rounded-md">
                          <Mail className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          <span className="flex-1 text-sm truncate">{recipient.email}</span>
                          <Select
                            value={recipient.filter}
                            onValueChange={(value: LeadNotificationFilter) => {
                              setFormData(prev => ({
                                ...prev,
                                lead_notification_emails: prev.lead_notification_emails.map((r, i) => 
                                  i === index ? { ...r, filter: value } : r
                                )
                              }));
                            }}
                          >
                            <SelectTrigger className="w-[140px] h-8 text-xs bg-background">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-background border shadow-lg z-50">
                              <SelectItem value="all">All Leads</SelectItem>
                              <SelectItem value="agent_only">Agent Only</SelectItem>
                              <SelectItem value="loan_officer_only">Loan Officer Only</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setFormData(prev => ({
                                ...prev,
                                lead_notification_emails: prev.lead_notification_emails.filter((_, i) => i !== index)
                              }));
                            }}
                            className="h-8 w-8 p-0 text-destructive hover:text-destructive flex-shrink-0"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Add new email with filter selection */}
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      placeholder="Enter email address"
                      value={newNotificationEmail}
                      onChange={(e) => setNewNotificationEmail(e.target.value)}
                      className="flex-1"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addNotificationEmail();
                        }
                      }}
                    />
                    <Select
                      value={newNotificationFilter}
                      onValueChange={(value: LeadNotificationFilter) => setNewNotificationFilter(value)}
                    >
                      <SelectTrigger className="w-[140px] bg-background">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-background border shadow-lg z-50">
                        <SelectItem value="all">All Leads</SelectItem>
                        <SelectItem value="agent_only">Agent Only</SelectItem>
                        <SelectItem value="loan_officer_only">Loan Officer Only</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={addNotificationEmail}
                    >
                      Add
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Press Enter or click "Add Email" to add an address
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Search Engine Indexing */}
        {site && (
          <Collapsible open={indexingOpen} onOpenChange={setIndexingOpen}>
            <Card className="mb-6">
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Search className="h-5 w-5" />
                      <div>
                        <CardTitle className="text-lg">Search Engine Indexing</CardTitle>
                        <CardDescription>Manage sitemap, robots.txt, and search engine submissions</CardDescription>
                      </div>
                    </div>
                    <ChevronDown className={`h-5 w-5 text-muted-foreground transition-transform ${indexingOpen ? 'rotate-180' : ''}`} />
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent>
                  <IndexingStatusPanel
                    siteId={site.id}
                    canonicalBaseUrl={formData.canonical_base_url}
                    primaryDomain={formData.primary_domain}
                    embedded
                  />
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        )}

        {/* Save Button */}
        <div className="flex justify-end mb-8">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Settings'
            )}
          </Button>
        </div>
      </main>
    </div>
  );
}